/**
 * The release — each release pull request with the owner's approving review or
 * the wait for it, and what is read before approving: the scenarios now built,
 * the contract the repository serves, and what a customer will see (the
 * `## What a customer will see` section of plan.md, contract.md §6).
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import { PrChip, StateBadge } from '@/components/changes/changeBadges';
import { Block, CODE, Rule, StageSection } from '@/components/changes/StageSection';
import { APPROVALS, formatMoment, repoFullName, whatACustomerWillSee, type Change } from '@/lib/changes/model';
import { docsApi } from '@/services/docsApi';
import { useDocText } from './useDocText';

/** Where a repository serves its contracts (Documentation Standard §5.1). */
const SERVED = [
  { type: 'openapi', pathPrefix: 'openapi/' },
  { type: 'asyncapi', pathPrefix: 'asyncapi/' },
] as const;

function useServedContract(repo: string): string[] | null {
  const [paths, setPaths] = useState<string[] | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all(SERVED.map((where) => docsApi.listDocs({ repo, ...where, view: 'summary' }))).then(
      (found) => { if (live) setPaths(found.flat().map((doc) => doc.path)); },
      () => { if (live) setPaths([]); },
    );
    return () => { live = false; };
  }, [repo]);

  return paths;
}

export function ReleaseSection({ change, current }: { change: Change; current: boolean }) {
  const { prs } = change.release;
  const { built, total } = change.behaviour.counts;
  const served = useServedContract(repoFullName(change));
  const plan = useDocText(change.plan.file?.docId);
  const customer = plan ? whatACustomerWillSee(plan) : [];

  return (
    <div data-part-release>
      <StageSection stage="release" title="The release" state={change.states.release} current={current}>
        {prs.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-release-waits>
            {change.archived ? 'Released.' : 'Not yet: no release pull request, preprod → the default branch, is open.'}
          </p>
        ) : (
          <ul className="space-y-2" data-release-prs>
            {prs.map((pr) => (
              <li
                key={`${pr.repo}#${pr.number}`}
                data-release-pr={`${pr.repo}#${pr.number}`}
                className="flex flex-col gap-1 rounded-lg border border-slate-100 p-3 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between"
              >
                <PrChip pr={pr} />
                <div className="flex items-center gap-1.5 text-xs">
                  {pr.approvingReview ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-emerald-700 dark:text-emerald-300">
                        approving review by {pr.approvingReview.by}, {formatMoment(pr.approvingReview.at)}
                      </span>
                    </>
                  ) : (
                    <>
                      <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                      <span className="text-amber-800 dark:text-amber-300">waiting for your approving review · a new push dismisses it</span>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 grid gap-3 lg:grid-cols-3" data-release-view>
          <Block label="The scenarios now built">
            <p className="text-sm text-slate-700 dark:text-slate-300" data-built-count>
              {total > 0 ? `${built} of ${total} built` : 'No feature file names this change.'}
            </p>
          </Block>
          <Block label="The contract served">
            {served === null ? null : served.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">This repository serves no contract.</p>
            ) : (
              <ul className="space-y-1 text-sm" data-served-contract>
                {served.map((path) => <li key={path}><code className={CODE}>{path}</code></li>)}
              </ul>
            )}
          </Block>
          <Block label="What a customer will see">
            {customer.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">plan.md has no section “What a customer will see”.</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-700 dark:text-slate-300" data-customer-sees>
                {customer.map((line) => <li key={line}>{line}</li>)}
              </ul>
            )}
          </Block>
        </div>

        {!change.archived && (
          <Block label={`${APPROVALS.two.label} — ${APPROVALS.two.what}`} className="mt-4 border-violet-200 bg-violet-50/40 dark:border-violet-900/60 dark:bg-violet-950/20">
            <div className="flex flex-wrap items-center gap-2" data-approval="two">
              <StateBadge state={change.states.release} />
              <span className="text-sm text-slate-700 dark:text-slate-300">{APPROVALS.two.how}</span>
            </div>
            <Rule>The approving review, given on GitHub, is the acceptance of everything the release carries. An agent never approves a release.</Rule>
          </Block>
        )}
      </StageSection>
    </div>
  );
}
